import { __awaiter } from "tslib";
import * as FullStory from '@fullstory/browser';
import { logger } from '@lusha/client-logger';
import { startIdleLogout } from '@lusha/idle-management';
import { reaction } from 'mobx';
import { flow } from 'mobx-state-tree';
import { getAccountIntegrations } from '../../../API/integrationsAPI';
import { fetchUserRole } from '../../../API/userAPI';
import { isLushaLists } from '../../../components/SaveContact/constants';
import { dismissNotifications } from '../../../hooks/useNotification';
import FEATURES from '../features';
const Actions = self => ({
    afterCreate: () => {
        reaction(() => self.isLoaded, () => {
            var _a;
            if (self.isLoaded) {
                if (!self.roleId) {
                    self.loadUserRole();
                }
                self.identifyFullstoryUser();
                if (self.isAccountAdmin && ((_a = self.billing) === null || _a === void 0 ? void 0 : _a.isPaid)) {
                    self.company.loadInfo();
                }
                if (self.idleTimeout.isEnabled && !self.idleTimeout.scriptAlreadyLoaded) {
                    startIdleLogout({ idleTime: self.idleTimeout.time });
                    self.idleTimeout.setScriptAlreadyLoaded(true);
                }
                self.fetchAndUpdateAccountIntegrations();
            }
        });
    },
    load: user => {
        try {
            dismissNotifications();
            self.setUser(user);
            self.isLoaded = true;
        }
        catch (e) {
            self.id = undefined;
        }
    },
    identifyFullstoryUser: () => {
        try {
            FullStory.identify(self.id.toString(), {
                displayName: `${self.firstName} ${self.lastName}`,
                email: self.email,
            });
        }
        catch (error) {
            logger.warn('Failed to identify FullStory user', error);
        }
    },
    loadUserRole: flow(function* loadUserRole() {
        var _a;
        if (self.isAccountSuspended) {
            return;
        }
        try {
            const response = yield fetchUserRole();
            self.setRoleId((_a = response.role) === null || _a === void 0 ? void 0 : _a.id);
        }
        catch (error) {
            self.setRoleId(null);
        }
    }),
    setIsAccountIntegrationLoaded: setter => {
        self.isAccountIntegrationLoaded = setter;
    },
    setIsFirstShow: setter => {
        self.isFirstShow = setter;
    },
    setIsFirstOutOfCredits: setter => {
        self.isFirstOutOfCredits = setter;
    },
    setUser: user => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        self.id = user.id;
        self.accountId = user.accountId;
        self.firstName = user.firstName;
        self.lastName = user.lastName;
        self.countryCode = user.countryCode;
        self.email = user.email;
        self.phone = (_a = user.phone) === null || _a === void 0 ? void 0 : _a.toString();
        self.hashedEmail = user.hashedEmail;
        self.industry = user.industry;
        self.isFirstShow = user.analytics ? !user.analytics.firstShowDate : false;
        self.isFirstOutOfCredits = user.analytics ? !user.analytics.firstOutOfCredits : false;
        self.isAccountAdmin = Boolean(user.admin);
        self.accountDomain = user.domain;
        self.groupName = (_b = user.group) === null || _b === void 0 ? void 0 : _b.name;
        self.features = user.features ? [...user.features] : [];
        self.isSearchByDataPointEnabled =
            (user.features || []).includes(FEATURES.SHOW_SPECIFIC_DATA_POINTS) &&
                !(user.features || []).includes(FEATURES.SCALE_FREE_TRIAL);
        self.isOneWalletEnabled = (user.features || []).includes(FEATURES.ONE_WALLET);
        self.priceBook = (_d = (_c = user.accountBillingPlans) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.priceBook;
        self.accountUnfreezeDate = ((_f = (_e = user.accountBillingPlans) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.endDate) || '';
        self.roleId = (_g = user === null || user === void 0 ? void 0 : user.role) === null || _g === void 0 ? void 0 : _g.id;
        self.idleTimeout.setIdleTimeout(((_h = user.accountSettings) === null || _h === void 0 ? void 0 : _h.idleLogoutTimeMinutes) || null);
        self.subscriptionStatus = ((_k = (_j = user.accountBillingPlans) === null || _j === void 0 ? void 0 : _j[0]) === null || _k === void 0 ? void 0 : _k.subscriptionStatus) || '';
        if (user === null || user === void 0 ? void 0 : user.account) {
            self.accountStatus = user.account.status || 'active';
            self.accountStatusReason = user.account.statusReason || '';
        }
        self.setBillingInfo(user.billing, user.accountBillingPlans);
        self.analytics.setInfo(user.analytics);
        self.initUBP(user.iubp);
    },
    setRoleId: roleId => {
        self.roleId = roleId;
    },
    isIntegrationConnected: integration => { var _a; return isLushaLists(integration) || ((_a = self.activeIntegrations.includes(integration)) !== null && _a !== void 0 ? _a : false); },
    updateIntegration: (integrationKey, status) => {
        if (status) {
            self.activeIntegrations.push(integrationKey);
        }
        else {
            self.activeIntegrations = self.activeIntegrations.filter(item => item !== integrationKey);
        }
    },
    setBillingInfo: (billing, accountBillingPlans) => {
        var _a, _b, _c, _d;
        if (!billing) {
            return;
        }
        const { category, isPaid, endDate, renewalType, renews = true } = billing;
        const stripeInvoiceLink = (_a = accountBillingPlans === null || accountBillingPlans === void 0 ? void 0 : accountBillingPlans[0]) === null || _a === void 0 ? void 0 : _a.stripeInvoiceLink;
        const stripePaymentMethodCountryCode = (_b = accountBillingPlans === null || accountBillingPlans === void 0 ? void 0 : accountBillingPlans[0]) === null || _b === void 0 ? void 0 : _b.stripePaymentMethodCountryCode;
        const billingPlanId = (_c = accountBillingPlans === null || accountBillingPlans === void 0 ? void 0 : accountBillingPlans[0]) === null || _c === void 0 ? void 0 : _c.billingPlanId;
        const startDate = (_d = accountBillingPlans === null || accountBillingPlans === void 0 ? void 0 : accountBillingPlans[0]) === null || _d === void 0 ? void 0 : _d.startDate;
        self.billing = {
            category,
            isPaid,
            endDate: new Date(endDate),
            stripeInvoiceLink,
            stripePaymentMethodCountryCode,
            renewalType,
            billingPlanId,
            renews,
            startDate: new Date(startDate),
        };
    },
    setIntegrations: accountIntegrations => {
        if (accountIntegrations) {
            self.activeIntegrations = accountIntegrations.map(item => item.integration.name);
        }
    },
    onCreditUsed: () => {
        --self.availableCredits;
    },
    fetchAndUpdateAccountIntegrations: () => __awaiter(void 0, void 0, void 0, function* () {
        if (!self.hasIntegrationPermissions) {
            return;
        }
        try {
            const accountIntegrations = yield getAccountIntegrations();
            self.setIntegrations(accountIntegrations);
            self.setIsAccountIntegrationLoaded(true);
        }
        catch (error) {
            logger.error(`Failed to fetch account integrations: ${JSON.stringify(error)}`, {
                accountId: self === null || self === void 0 ? void 0 : self.accountId,
                userId: self === null || self === void 0 ? void 0 : self.id,
                error,
            });
        }
    }),
    initUBP: ubp => {
        if ((ubp === null || ubp === void 0 ? void 0 : ubp.extensionBulkShows) && ubp.extensionBulkShows.availableCredits !== 'unlimited') {
            self.ubp = {
                bulkShowCredits: ubp.extensionBulkShows.availableCredits,
                totalBulkShowCredits: ubp.extensionBulkShows.totalCredits,
            };
        }
    },
    setAccountIntentTopicsCount(value) {
        self.accountIntentTopicsCount = value;
    },
    setBulkAvailableCredits(value) {
        self.bulkAvailableCredits = value !== null && value !== void 0 ? value : 0;
    },
    setAccountIntentTopicsLoaded(value) {
        self.accountIntentTopicsLoaded = value;
    },
});
export default Actions;
